/**
 * apply-theme.ts — DOM injection layer for dynamic theming.
 *
 * Provides two main functions:
 *   - applyTheme()  : generates tokens from a source color and sets them
 *                     as CSS custom properties on document.documentElement
 *   - clearTheme()  : removes all runtime overrides, reverting to the
 *                     SCSS-compiled defaults
 *
 * How it works:
 *   Setting `documentElement.style.setProperty('--rd-sys-color-*', value)`
 *   overrides the stylesheet-declared `:root { --rd-sys-color-*: ... }`
 *   because inline custom properties have higher specificity.
 *   Calling `removeProperty()` reverts to the stylesheet default.
 *
 * Works with the existing [data-theme='dark'] toggle — when dark mode is
 * active, we simply generate and inject the dark-mode token set instead.
 */

import { getEmbedCssParams } from '@thoughtspot/embed-util';
import { GeneratedTheme, generateTheme } from './theme-generator';
import {
    COMP_TOKEN_PREFIX,
    COMPONENT_TOKEN_NAMES,
    SYS_TOKEN_PREFIX,
    SYSTEM_TOKEN_NAMES,
} from './token-names';

// =========================================================================
// Brand color normalization
// =========================================================================

const VALID_BRAND_HEX = /^#[0-9a-f]{6}$/;

/**
 * Normalize a persisted/event-supplied brand color to "#rrggbb", or null if
 * it cannot be interpreted as a hex color.
 *
 * Legacy formats provably exist in stored appPanelColor values (the DARK
 * sentinel itself is "#1D232FFF"), and the Material parser this engine
 * replaced accepted 3- and 8-digit hex — so alpha suffixes and shorthand
 * must keep theming instead of throwing during app bootstrap. This is also
 * the input-validation choke point before any value reaches
 * style.setProperty or the embed CSS <style> serialization.
 */
export function normalizeBrandColor(input: string): string | null {
    if (typeof input !== 'string') return null;
    let hex = input.trim().toLowerCase();
    if (!hex.startsWith('#')) hex = `#${hex}`;
    // #rgb / #rgba shorthand → expand (alpha digit dropped below)
    if (/^#[0-9a-f]{3,4}$/.test(hex)) {
        hex = `#${hex
            .slice(1, 4)
            .split('')
            .map(c => c + c)
            .join('')}`;
    }
    // #rrggbbaa → strip alpha
    if (/^#[0-9a-f]{8}$/.test(hex)) {
        hex = hex.slice(0, 7);
    }
    return VALID_BRAND_HEX.test(hex) ? hex : null;
}

// =========================================================================
// External variable bridge
// =========================================================================
//
// Many legacy components use --ts-var-* CSS variables (the "embed
// customization" layer) instead of the --rd-* radiant design tokens.
// These chain through --ts-var-root-color, which is declared as `initial`
// in _external-variables.scss. When `initial` (guaranteed-invalid in CSS
// custom properties), vars resolve to fallbacks — but some elements in the
// inheritance chain have NO radiant fallback, causing dark text on dark bg.
//
// We bridge this gap by also setting the key --ts-var-* variables when
// a dynamic theme is applied. This single variable cascades to:
//   - --ts-var-search-bar-text-font-color (search token text)
//   - --ts-var-axis-data-label-color (KPI hero text)
//   - --ts-var-nav-color, --ts-var-nav-text-color
//   - --ts-var-chip-color, --ts-var-chip--hover-color
//   - --ts-var-menu-color, --ts-var-dialog-body-color, etc.
//   - Plus ~30 more variables declared in _external-variables.scss
//
// See: js/ts-packages/radiant-styles/css/_external-variables.scss
// =========================================================================

/**
 * External variables that need to be bridged to the radiant token system.
 * Maps an external CSS variable name to the system or component token it
 * should mirror. Every referenced token must resolve to an OPAQUE hex —
 * legacy --ts-var-* consumers cannot be assumed to handle rgba() values.
 *
 * --ts-var-root-color cascade audit: the vars that chain from it in
 * _external-variables.scss (nav-text, search-bar text, chip text, menu,
 * dialog, axis labels, viz title, home-card, liveboard-header font, etc.)
 * are all plain-text roles for which content-primary is correct. The two
 * button vars below are the exceptions — semantically they are NOT text
 * colors, so the chain would wrongly flatten them to content-primary
 * (e.g. tertiary/link buttons turning white in dark mode).
 */
const EXTERNAL_VARIABLE_BRIDGE: ReadonlyArray<{
    /** The --ts-var-* CSS variable to set */
    externalVar: string;
    /** The system token name whose value should be used */
    systemToken?: string;
    /** The component token name whose value should be used */
    componentToken?: string;
    /**
     * Skip this pin when the embed customer set --ts-var-root-color:
     * _external-variables.scss cascades root-color into this var, and an
     * inline pin on <html> would sever that documented cascade.
     */
    respectRootCascade?: boolean;
}> = [
    // Root text color — cascades to ~30+ --ts-var-* variables
    // (nav, chip, menu, dialog, axis labels, search bar, etc.)
    { externalVar: '--ts-var-root-color', systemToken: 'content-primary' },

    // Root background — app shell background (static fallback is
    // background-sunken in authenticated-app-view.module.scss)
    {
        externalVar: '--ts-var-root-background',
        systemToken: 'background-sunken',
    },

    // Primary button text: _external-variables.scss leaves this `initial`,
    // and the radiant fallback is content-inverse — which is NOT
    // contrast-checked against a custom brand color. Bridge it to the
    // generator's contrast-picked CTA text so labels stay readable (e.g.
    // dark text on a light-yellow brand CTA).
    {
        externalVar: '--ts-var-button--primary-color',
        componentToken: 'button-primary-content',
        respectRootCascade: true,
    },

    // Tertiary/link button text: chains from --ts-var-root-color in
    // _external-variables.scss, which the bridge above sets to
    // content-primary — flattening every text button (e.g. the filter
    // dialog's "Reset to default") to plain white/black. Pin it back to
    // the brand text color the radiant fallback intends.
    {
        externalVar: '--ts-var-button--tertiary-color',
        systemToken: 'content-brand',
        respectRootCascade: true,
    },

    // Navigation bar colors
    {
        externalVar: '--ts-var-nav-background',
        systemToken: 'background-on-base',
    },
    { externalVar: '--ts-var-nav-color', systemToken: 'content-primary' },

    // Sage (search bar) header background
    {
        externalVar: '--ts-var-sage-bar-header-background-color',
        systemToken: 'background-base',
    },

    // Menu dropdown surface & text
    {
        externalVar: '--ts-var-menu-background',
        systemToken: 'background-raised',
    },
    { externalVar: '--ts-var-menu-color', systemToken: 'content-primary' },

    // Dialog / modal surfaces & text — each var mirrors the token the
    // radiant Modal uses as its own fallback (modal.module.scss): header
    // and body sit on background-base, the footer on background-on-base.
    // Flattening them to one token makes the footer indistinguishable
    // from the body.
    {
        externalVar: '--ts-var-dialog-header-background',
        systemToken: 'background-base',
    },
    {
        externalVar: '--ts-var-dialog-body-background',
        systemToken: 'background-base',
    },
    {
        externalVar: '--ts-var-dialog-body-color',
        systemToken: 'content-primary',
    },
    {
        externalVar: '--ts-var-dialog-header-color',
        systemToken: 'content-primary',
    },
    {
        externalVar: '--ts-var-dialog-footer-background',
        systemToken: 'background-on-base',
    },

    // Liveboard surfaces — pinned to the same tokens the static SCSS
    // fallbacks use (layout: sunken page background; tile: base surface,
    // matching pinboard-content.module.scss — raised would render tiles
    // lighter than the header in dark mode).
    {
        externalVar: '--ts-var-liveboard-layout-background',
        systemToken: 'background-sunken',
    },
    {
        externalVar: '--ts-var-liveboard-tile-background',
        systemToken: 'background-base',
    },

    // Viz title / description text
    { externalVar: '--ts-var-viz-title-color', systemToken: 'content-primary' },
    {
        externalVar: '--ts-var-viz-description-color',
        systemToken: 'content-secondary',
    },
];

// =========================================================================
// App-level theme metadata CSS variables
// =========================================================================
//
// These CSS variables are the single source of truth for the active theme
// at the CSS level. Any code (JS or CSS) can read them to know:
//   1. What type of theme is active (custom / dark / two-tone / none)
//   2. What the source brand color is (only set when type=custom)
//
// Usage in JS:
//   getComputedStyle(document.documentElement).getPropertyValue('--ts-app-source-color')
//
// Usage in CSS (future):
//   background: var(--ts-app-source-color, #2770ef);
// =========================================================================

/** The CSS variable that holds the current source/brand color hex. */
const APP_SOURCE_COLOR_VAR = '--ts-app-source-color';

/** The CSS variable that holds the active theme type (custom / dark / two-tone / none). */
const APP_THEME_TYPE_VAR = '--ts-app-theme-type';

// =========================================================================
// Internal state
// =========================================================================

/** Tracks whether a custom theme is currently active. */
let customThemeActive = false;

/** Stores the current source color for re-application on mode switch. */
let currentSourceHex: string | null = null;

/** Cached generated theme to avoid re-computation on mode toggle. */
let cachedTheme: GeneratedTheme | null = null;

/**
 * The set of bridged --ts-var-* names the EMBED CUSTOMER owns, snapshotted
 * once before this module ever writes to the embed CSS store.
 *
 * Why a snapshot rather than a live getEmbedCssParams() check: the apply flow
 * feeds applyTheme's returned vars back into the embed store
 * (addCustomCssElements → setEmbedCssParams, which *replaces* the store), so
 * after the first apply a live check would see our OWN echoed vars and could
 * not tell them from a genuine customer override — a false positive that
 * makes later applies (e.g. a light↔dark toggle via reapplyThemeForMode) skip
 * bridged vars and leave stale-mode colors on the page. The embed customer's
 * customCSS is set during embed bootstrap, before the authenticated layout
 * mounts and applies a brand theme, so a snapshot taken on the first apply —
 * before our first setProperty — captures exactly the customer's set and
 * never drifts thereafter.
 */
let embedOwnedVars: Set<string> | null = null;

/** Bridged var names to consult for customer ownership (bridge + cascade root). */
function ownedVarCandidates(): string[] {
    return [
        '--ts-var-root-color',
        ...EXTERNAL_VARIABLE_BRIDGE.map(entry => entry.externalVar),
    ];
}

/** Snapshot customer-owned bridged vars once, before we write anything. */
function ensureEmbedOwnedSnapshot(): void {
    if (embedOwnedVars) return;
    embedOwnedVars = new Set(
        ownedVarCandidates().filter(varName => !!getEmbedCssParams(varName)),
    );
}

/** True when the embed customer owns this variable (from the snapshot). */
function isCustomerOwned(varName: string): boolean {
    return embedOwnedVars?.has(varName) ?? false;
}

// =========================================================================
// Public API
// =========================================================================

/**
 * Generate a full M3 color theme from a source color and apply it to the DOM.
 *
 * This sets --rd-sys-color-* and --rd-comp-color-* CSS custom properties
 * on document.documentElement, which override the SCSS defaults and cause
 * every component using these tokens to update instantly.
 *
 * @param sourceHex - Source/brand color hex (e.g., "#2770ef"); legacy
 *                    formats (#rgb, #rrggbbaa, missing '#') are normalized,
 *                    anything else is logged and ignored (no-op).
 * @param options   - isDark: force dark mode tokens
 *
 * @example
 * ```ts
 * // Apply a purple theme in current mode
 * applyTheme('#8C62F5');
 *
 * // Apply a purple theme explicitly in dark mode
 * applyTheme('#8C62F5', { isDark: true });
 * ```
 */
export function applyTheme(
    rawSourceHex: string,
    options: { isDark?: boolean } = {},
): Record<string, string> {
    // Collects every CSS variable set by this call. Returned to the
    // caller so it can feed them into addCustomCssElements (the embed
    // CSS infrastructure) without scraping the DOM.
    const appliedVars: Record<string, string> = {};

    // Validate/normalize before anything touches the DOM: a bad persisted
    // color must degrade to the default theme, never crash app bootstrap
    // (blink-v2 CLAUDE.md: all init-path errors must be caught and logged).
    const sourceHex = normalizeBrandColor(rawSourceHex);
    if (!sourceHex) {
        // eslint-disable-next-line no-console
        console.error(
            `[dynamic-theme] Ignoring invalid brand color "${rawSourceHex}"`,
        );
        return appliedVars;
    }

    // Snapshot the embed customer's owned vars before we touch the store.
    ensureEmbedOwnedSnapshot();

    // Determine current mode from data-theme attribute if not explicitly set
    const isDark =
        options.isDark ??
        document.documentElement.getAttribute('data-theme') === 'dark';

    // Only regenerate if the source color changed
    if (sourceHex !== currentSourceHex || cachedTheme === null) {
        cachedTheme = generateTheme(sourceHex);
    }

    // Pick the correct mode's tokens
    const mode = isDark ? 'dark' : 'light';

    // Guard: cachedTheme is guaranteed non-null here (either just generated
    // above, or was already non-null from a previous call), but we guard
    // explicitly to satisfy the linter's no-non-null-assertion rule.
    if (!cachedTheme) {
        return appliedVars;
    }

    const systemTokens = cachedTheme.system[mode];
    const componentTokens = cachedTheme.component[mode];

    const root = document.documentElement;

    // Inject system tokens
    Object.entries(systemTokens).forEach(([name, value]) => {
        const varName = `${SYS_TOKEN_PREFIX}${name}`;
        root.style.setProperty(varName, value);
        appliedVars[varName] = value;
    });

    // Inject component tokens. (The embed-customer precedence for the
    // generator-only button-primary-disabled token is handled declaratively
    // by the var() fallback order in radiant-react button.module.scss —
    // --ts-var-button--primary-background is read first there.)
    Object.entries(componentTokens).forEach(([name, value]) => {
        const varName = `${COMP_TOKEN_PREFIX}${name}`;
        root.style.setProperty(varName, value);
        appliedVars[varName] = value;
    });

    // Bridge external --ts-var-* variables that don't use radiant fallbacks.
    // Without this, components using --ts-var-root-color (KPI, chart selector,
    // search tokens, etc.) get dark text on dark backgrounds in dark mode.
    //
    // IMPORTANT: Skip bridging when the embed CUSTOMER owns the variable —
    // customer overrides take priority over dynamic theming. Ownership comes
    // from the snapshot (embedOwnedVars), not a live store check, so re-applies
    // (e.g. a light↔dark toggle) always refresh our own bridged vars instead
    // of freezing the first mode's colors.
    EXTERNAL_VARIABLE_BRIDGE.forEach(
        ({ externalVar, systemToken, componentToken, respectRootCascade }) => {
            if (isCustomerOwned(externalVar)) {
                return;
            }
            // Leaf vars that _external-variables.scss derives from
            // --ts-var-root-color: when the customer set the cascade ROOT,
            // let their cascade win instead of pinning the leaf inline.
            if (respectRootCascade && isCustomerOwned('--ts-var-root-color')) {
                return;
            }

            const value = systemToken
                ? systemTokens[systemToken]
                : componentTokens[componentToken ?? ''];
            if (value) {
                root.style.setProperty(externalVar, value);
                appliedVars[externalVar] = value;
            }
        },
    );

    // Set app-level metadata CSS variables.
    // --ts-app-source-color is THE single source of truth for the brand color.
    // Downstream CSS or JS can reference it to know the active brand color.
    root.style.setProperty(APP_SOURCE_COLOR_VAR, sourceHex);
    root.style.setProperty(APP_THEME_TYPE_VAR, 'custom');
    appliedVars[APP_SOURCE_COLOR_VAR] = sourceHex;
    appliedVars[APP_THEME_TYPE_VAR] = 'custom';

    // Update internal state
    customThemeActive = true;
    currentSourceHex = sourceHex;

    return appliedVars;
}

/**
 * Remove all runtime theme overrides, reverting to SCSS-compiled defaults.
 *
 * Two-pass approach:
 *   1. Remove known token names from the named arrays (fast path).
 *   2. Sweep all inline properties on :root and remove any that match our
 *      prefixes (--rd-sys-color-*, --rd-comp-color-*, --ts-var-*, --ts-app-*).
 *      This catches properties that may have been set outside the named
 *      arrays or by external code paths.
 *
 * After removal the browser falls back to the stylesheet-declared values.
 */
export function clearTheme(): void {
    const root = document.documentElement;

    // --- Pass 1: Named token removal (fast path) -------------------------

    // Remove system token overrides
    SYSTEM_TOKEN_NAMES.forEach(name => {
        root.style.removeProperty(`${SYS_TOKEN_PREFIX}${name}`);
    });

    // Remove component token overrides
    COMPONENT_TOKEN_NAMES.forEach(name => {
        root.style.removeProperty(`${COMP_TOKEN_PREFIX}${name}`);
    });

    // Remove bridged external variables that WE set (i.e. not customer-owned,
    // per the snapshot). Genuine embed-customer overrides live in the embed
    // <style> element, never as inline props here, so this leaves them intact.
    EXTERNAL_VARIABLE_BRIDGE.forEach(({ externalVar }) => {
        if (!isCustomerOwned(externalVar)) {
            root.style.removeProperty(externalVar);
        }
    });

    // Clear app-level metadata CSS variables
    root.style.removeProperty(APP_SOURCE_COLOR_VAR);
    root.style.removeProperty(APP_THEME_TYPE_VAR);

    // --- Pass 2: Defensive sweep -----------------------------------------
    // Iterate ALL inline style properties on :root and remove any that match
    // our known prefixes. This guarantees nothing is left behind, even if the
    // named arrays are out of sync or if other code set extra variables.
    //
    // IMPORTANT: a --ts-var-* prop is removed unless the snapshot marks it
    // embed-customer-owned. Anything the customer genuinely set lives in the
    // embed <style> element, not inline, so this only ever clears our own
    // inline bridged vars.
    const allInlineProps = Array.from(
        { length: root.style.length },
        (_, i) => root.style[i],
    );
    const propsToRemove = allInlineProps.filter(prop => {
        if (
            prop.startsWith(SYS_TOKEN_PREFIX) ||
            prop.startsWith(COMP_TOKEN_PREFIX) ||
            prop.startsWith('--ts-app-')
        ) {
            return true;
        }
        if (prop.startsWith('--ts-var-')) {
            return !isCustomerOwned(prop);
        }
        return false;
    });
    propsToRemove.forEach(prop => {
        root.style.removeProperty(prop);
    });

    // Reset internal state. The embed-owned snapshot is intentionally NOT
    // reset: the customer's owned vars don't change because we cleared our
    // theme, and re-taking it after we've polluted the store would be wrong.
    customThemeActive = false;
    currentSourceHex = null;
    cachedTheme = null;
}

/**
 * Re-apply the current custom theme for a different mode (light/dark).
 *
 * Call this from the dark mode toggle when a custom theme is active.
 * Uses the cached theme (no re-generation) for instant mode switch.
 *
 * @param isDark - Whether to apply dark mode tokens
 */
export function reapplyThemeForMode(isDark: boolean): void {
    if (!customThemeActive || !currentSourceHex) {
        return; // No custom theme active — nothing to do
    }
    applyTheme(currentSourceHex, { isDark });
}

/**
 * Check if a custom theme is currently active.
 *
 * Useful for the dark mode toggle to know whether it should
 * call reapplyThemeForMode().
 */
export function isCustomThemeActive(): boolean {
    return customThemeActive;
}

/**
 * Get the current source color hex, or null if no custom theme is active.
 */
export function getCurrentSourceColor(): string | null {
    return currentSourceHex;
}

/**
 * Read the active source color from the CSS variable on :root.
 * Returns null if no custom theme is active.
 * Useful for code that doesn't have access to the JS module state.
 */
export function getSourceColorFromCSS(): string | null {
    const value = getComputedStyle(document.documentElement)
        .getPropertyValue(APP_SOURCE_COLOR_VAR)
        .trim();
    return value || null;
}

// Re-export the CSS variable names for consumers that need them
export { APP_SOURCE_COLOR_VAR, APP_THEME_TYPE_VAR };

/**
 * Test-only: reset all module-level state, including the embed-owned snapshot
 * (which is intentionally NOT reset by clearTheme in production). Lets each
 * spec start from a clean slate.
 */
export function resetDynamicThemeStateForTests(): void {
    customThemeActive = false;
    currentSourceHex = null;
    cachedTheme = null;
    embedOwnedVars = null;
}
